// Type definition for shared serialized tag data
import { AbTastyJsApi } from '@abtasty/tag-api-types';
import { CustomEvent } from '@abtasty/tag-api-types/dist/api/customEvent';
import { AccountData as TagAccountData } from '@abtasty/tag-api-types/dist/account/index';
import {
  ActionTrackingCollection,
  Campaign,
  CampaignAdditionalTypes,
} from '@abtasty/tag-api-types/dist/campaign/index';
import { Audience } from '@abtasty/tag-api-types/dist/campaign/audience';

import { UAResponse } from '@src/container/custom';

export type TagData = SerializedABTasty & {
  testsOnPage: TestsOnPage;
  geoloc: Geoloc | null;
  parsedUserAgent?: ParsedUserAgent;
  ABTastyCookie: string | null;
  ABTastySessionCookie: string | null;
  ABTastyData: {
    ActionTracking?: ActionTracking[];
    VisitedPages?: VisitedPage[];
    items?: Item[];
    transactions?: TransactionType[];
  } | null;
  ABTastyUA: ABTastyUA | null;
  browserLanguage: string;
  screenSize: number;
  urlHistory: {
    current: string;
    previous: string;
  };
  hasRefreshedJsCache: AbTastyJsApi.General['jsCacheRefreshed'];
};
type VisitedPage = {
  url: string;
  time: number;
  title: string;
  visite: number;
};
type ActionTracking = {
  time: number;
  name: string;
  value: number;
};
export type TransactionType = {
  coupon: string;
  id: string;
  payment?: string;
  shipping?: string;
  time: number;
  value: number;
  shipping_cost?: number;
  tax?: number;
  currency?: string;
  affiliation?: string;
};

export type ABTastyUA = {
  type: string;
  os: {
    name: string;
    version: string;
  };
  browser: {
    name: string;
    version: string;
  };
  is_bot?: boolean;
};
export type Item = {
  category: string;
  local_revenue: number;
  name: string;
  quantity: number;
  revenue: number;
  sku: string;
  time: number;
  transactionId: string;
};

type AccountRights = {
  emotionAiEnabled: boolean; // DTK tag
  collectOnlyInternalEmotionAi: boolean;
  fullInternalEmotionAi: boolean;
  recoAndMerchEnabled: boolean;
};

export type SerializedABTasty = {
  accountData: {
    accountSettings: AccountSettings;
    tests: { [id: number]: AccountDataTest };
    accountLevelTrackings: TagAccountData['accountLevelTrackings'];
    accountRights: AccountRights;
  };
  ADBlockEnabled: boolean;
  cnilReady: boolean;
  consentReady: boolean;
  datalayerEnabled: boolean;
  omnitureProcessed: boolean;
  pendingRedirection: boolean;
  pendingUAParser: boolean;
  results: Record<number, TagResultTest>;
  started: boolean;
  temporaryCookieValues?: {
    ABTastySession?: {
      config: CookieConfig;
      value: string;
    };
  };
  urlHistory?: {
    previous: string;
    current: string;
  };
  visitor: { id: string };
  tagInfos: {
    commitHash: string;
    version: string;
    enabledFlagshipExperiments: {
      name: string;
      value: string | number | boolean;
    }[];
  };
  eventState?:
    | Record<CustomEvent.Name, { status: CustomEvent.Status; detail?: CustomEvent.Detail }>
    | Record<string, never>;
};

export type TestOnPage = TagResultTest & {
  testDatas: TagTest & { variations: Record<string, Variation> };
};

export type TestsOnPage = { [id: number]: TestOnPage };

export type AccountDataTest = TagTest & {
  siblings?: number[];
} & ({ isAsync?: 0; variations: Record<string, Variation> } | { isAsync: 1; variations: undefined });

export type Geoloc = {
  ip_address: string;
  country_iso_code: string;
  weather?: {
    description: string;
    main: string;
    temperature: number;
  };
};

export type ParsedUserAgent = UAResponse;

export type CookieConfig = {
  domain?: string;
  expires: string;
  path: string;
  samesite: string;
  secure: boolean;
};

export type AccountSettings = {
  accountIframeException: boolean;
  accountName: string;
  addJquery: boolean;
  ajaxAutoReload: boolean;
  apiTokenWeborama: string | null;
  cedexisRadar: number;
  customCookieDomain: string | null;
  customCookiePath: string;
  datalayerMaxToSend: number;
  datalayerVariable: string;
  eulerianPixelURL: string | null;
  excludeIE: boolean;
  frameworkVersion: string;
  getAlwaysWeborama: boolean | null;
  globalCode: string;
  globalCodeOnDocReady: boolean;
  hashMrasnAllowed: boolean;
  heatmapRight: boolean;
  id: number;
  identifier: string;
  isSecureCookie: boolean;
  jqueryVarName: string | null;
  kruxNamespace?: string | null;
  omnitureIntegration: number;
  oneVisitorOneTest: boolean;
  pack: string;
  quota: number;
  runAsThread: number;
  sessionRecordingRights: boolean;
  storageMode: string;
  tealiumAccountName?: string | null;
  tealiumProfileName?: string | null;
  toleranceParams: [];
  toleranceRegex?: string | null;
  waitForConsent: {
    data: string | { name: string; value: string; condition?: number } | null;
    isStrict: boolean;
    mode: string;
  };
};

/** See Doc: https://developers.abtasty.com/docs/tag/tag-campaigns-types#campaign-status */
export type TestStatus = TestStatusAccepted | TestStatusWaiting | TestStatusRejected | TestStatusForced;

/** See Doc: https://developers.abtasty.com/docs/tag/tag-campaigns-types#campaign-status */
export enum TestStatusAccepted {
  /** Campaign is applied on the page.	 */
  accepted = 'accepted',
  /** Campaign is applied on the page but the variation modifications are not applied as it comes from a redirected campaign.	*/
  acceptedByRedirection = 'accepted_by_redirection',
}

/** See Doc: https://developers.abtasty.com/docs/tag/tag-campaigns-types#campaign-status */
export enum TestStatusWaiting {
  /** Tag is trying to resolve the targeting every 16ms. */
  pending = 'pending',
  /** Tag is currently checking the targeting. */
  checking = 'currently_checking',
  /** Tag is waiting for other perso subsegments to be checked before checking the current one. */
  waitingForSubsegmentCheck = 'other_subsegment_is_checking',
  /** Tag is waiting for manual activation thanks to ABTastyStartTest(). */
  targetByEventPending = 'target_by_event_pending',
  /** Tag is waiting for asynchronous targeting code resolution */
  waitingCodeResolution = 'waiting_code_resolution',
}

/** See Doc: https://developers.abtasty.com/docs/tag/tag-campaigns-types#campaign-status */
export enum TestStatusRejected {
  /** (Deprecated) Campaign is not applied and targeting success is false. */
  rejected = 'rejected',
  /** (Deprecated) Campaign is not applied and audience targeting success is false. */
  audienceRejected = 'audience_rejected',
  /** Campaign is not applied and "Where to display the test" criterias success is false. */
  targetPagesRejected = 'target_pages_rejected',
  /** Campaign is not applied and QA mode criterias success is false. */
  qaParametersRejected = 'qa_parameters_rejected',
  /** Campaign is not applied and trigger criterias success is false. */
  triggerRejected = 'trigger_rejected',
  /** Campaign is not applied and audience segment criterias success is false. */
  segmentRejected = 'segment_rejected',
  /** Campaign is not applied because the display frequency is not met. */
  displayFrequency = 'display_frequency_rejected',
  /** Campaign is not applied due to untracked traffic from the traffic allocation configuration or campaign is using deferred option and Tag doesn't successfully load the campaign configuration. */
  trafficRejected = 'traffic_rejected',
  /** Campaign is not applied because another campaign have already been applied during the session (only for AB Test, Multi-pages and Multi-variates). */
  oneVisitorOneTest = 'one_visitor_one_test',
  /** Campaign can't be applied because another scenario of the same personalization is already applied (only for personalization). */
  anotherSubsegmentAlreadyStarted = 'another_subsegment_already_started',
  /** Campaign is not applied because it's a master campaign without any modifications. */
  masterCampaignNotChecked = 'master_campaign_not_checked',
  /** (For deferred campaign) Variation data has take more than 3 seconds to be loaded so tag stop and don't execute the campaign. */
  timeout = 'timeout',
  /** (For deferred campaign) Variation data has not been loaded due to a network error or network blockage. */
  deferredFailedLoading = 'deferred_loading_failed',
  /** Campaign is not applied because a campaign with a higher priority level has already been accepted. */
  notPrioritizedYet = 'not_prioritized_yet',
  /** Campaign is not applied because this campaign type is not supposed to be executed without consent. */
  campaignTypeRejectedByConsent = 'campaign_type_rejected_by_consent',
  /** Campaign is not applied because it has a geolocation based targeting and visitor didn't consent for geolocation usage. */
  geolocationRejectedByConsent = 'geolocation_rejected_by_consent',
  /** Campaign is not applied because you are using the Heatmap screenshot mode. */
  screenshotModeRejected = 'screenshot_mode_rejected',
}

export enum TestStatusForced {
  /* Campaign targeting has been forced to be accepted and applied on page */
  forceAccepted = 'force_accepted',
  /* Campaign targeting has been forced to be rejected and not applied on page */
  forceRejected = 'force_rejected',
}

export enum TestStatusCategory {
  accepted = 'accepted',
  waiting = 'waiting',
  rejected = 'rejected',
  forced = 'forced',
}

export const enum TestType {
  ab = 'ab',
  multipage = 'multipage',
  multivariate = 'multivariate',
  mastersegment = 'mastersegment',
  subsegment = 'subsegment',
}

export const enum TestSubType {
  simplePersonalization = 'sp',
  multipagePersonalization = 'mpp',
  multiexperiencePersonalization = 'mep',
  patch = 'patch',
  ab = 'ab',
}

type TestTargetings = any; // TO DO

export type TagResultTest = {
  name: string;
  status: TestStatus;
  targetings: TestTargetings;
  triggerMode?: 'some' | 'all';
  segmentMode?: 'some' | 'all';
  type: TestType;
  sub_type?: TestSubType;
  variationID?: number;
  variationName: string | null;
};

type TagTest = {
  actionTrackings?: ActionTrackingCollection;
  additionalType?: CampaignAdditionalTypes;
  dynamicTrafficGoalId?: number | null;
  dynamicTrafficModulation?: number;
  dynamicTestedTraffic?: number;
  exclusionGroupId?: number;
  globalCode?: string;
  id: number;
  isAsync?: 0 | 1;
  name: string;
  parentID?: number;
  priority?: number;
  scopes?: {
    urlScope?: URLScope[];
    codeScope?: any[];
    selectorScope?: any[];
    cookieScope?: any[];
    ipScope?: IPScope[];
    qaUrlParameterEnabled?: boolean;
  };
  status: TestStatus;
  targetingMode?: string;
  traffic?: number;
  type: TestType;
  sub_type?: TestSubType;
  variations?: Record<string, Variation>;
  asyncVariationInfoById?: Record<string, Variation>;
  QAModeActive?: boolean;
  targetingDetails?: any;
  targetings?: TestTargetings;
  variationID?: number;
  variationName?: string | null;
  displayFrequencyType: FrequencyType;
  displayFrequencyUnit: FrequencyUnit;
  displayFrequencyValue: number;
  segmentMode?: 'some' | 'all';
  triggerMode?: 'some' | 'all';
  audienceSegment?: Audience[];
  audienceTrigger?: Audience[];
  children?: number[];
  widgets?: Campaign['widgets'];
};

// TODO => Should come from api-client
export enum FrequencyType {
  any = 'any',
  once = 'once',
  oncePerSession = 'once_per_session',
  regular = 'regular',
}

// TODO => Should come from api-client
export enum FrequencyUnit {
  day = 'day',
  week = 'week',
  session = 'session',
}

type URLScope = {
  condition: number;
  include: boolean;
  value: string;
};
type IPScope = {
  from: string;
  include: boolean;
  range: boolean;
  to: string;
};
export type Variation = {
  id: number;
  masterVariationId: number;
  modifications?: {
    id: number;
    oldValue?: string;
    selector: string;
    type: string;
    value?: string;
  }[];
  name: string;
  redirections?: [];
  traffic: number;
};
